import { UserContextProps } from "../../../../Context/UserContext";
import { CustomDimension } from "../../../../Models/CustomDimension";
import EnvironmentConfiguration from "../../../Constants/EnvironmentConfiguration";
import LeadResultType from "../../../Constants/LeadResultType";
import { IDispositionScenarioSessionProps } from "./DispositionScenario";

export type IDispositionScenarioSessionFilterProps = IDispositionScenarioSessionProps &
  Pick<UserContextProps, "leadResultTypes" | "disqualifyingReasons">;

/** A configuration which identifies the not-allowed lead result type options given the specified constraint is met. */
export class ScenarioConditionalExclusion {
  /** The constraint that must be met for the exclusion configuration to be respected. */
  readonly constraint: (props: IDispositionScenarioSessionProps) => boolean;
  /** The type options that should be excluded if the constraint is met. */
  readonly excludedOptions: LeadResultType[];

  constructor(constraint: (props: IDispositionScenarioSessionProps) => boolean, excludedOptions: LeadResultType[]) {
    this.constraint = constraint;
    this.excludedOptions = excludedOptions;
  }
}

/** A configuration which identifies the allowed lead result type options given the specified constraint is met. */
export class ScenarioConditionalInclusion {
  /** The constraint that must be met for the inclusion configuration to be respected. */
  readonly constraint: (props: IDispositionScenarioSessionProps) => boolean;
  /** The type options that should be included if the constraint is met. */
  readonly includedOptions: LeadResultType[];

  constructor(constraint: (props: IDispositionScenarioSessionProps) => boolean, includedOptions: LeadResultType[]) {
    this.constraint = constraint;
    this.includedOptions = includedOptions;
  }
}

/** A configuration which filters the included lead result type options from the full list of options
 * given the specified constraint is met.
 */
export class ScenarioFilteredInclusion {
  /** The constraint that must be met for the inclusion configuration to be respected. */
  readonly constraint: (props: IDispositionScenarioSessionFilterProps) => boolean;
  /** The type options that should be included if the constraint is met. */
  readonly inclusionFilter: (
    props: IDispositionScenarioSessionFilterProps,
    options: CustomDimension<number>[]
  ) => CustomDimension<number>[];

  constructor(
    constraint: (props: IDispositionScenarioSessionFilterProps) => boolean,
    inclusionFilter: (
      props: IDispositionScenarioSessionFilterProps,
      inclusionFilter: CustomDimension<number>[]
    ) => CustomDimension<number>[]
  ) {
    this.constraint = constraint;
    this.inclusionFilter = inclusionFilter;
  }
}

/** A configuration which filters the excluded lead result type options from the full list of options
 * given the specified constraint is met.
 */
export class ScenarioFilteredExclusion {
  /** The constraint that must be met for the inclusion configuration to be respected. */
  readonly constraint: (props: IDispositionScenarioSessionFilterProps) => boolean;
  /** The type options that should be included if the constraint is met. */
  readonly exclusionFilter: (
    props: IDispositionScenarioSessionFilterProps,
    options: CustomDimension<number>[]
  ) => CustomDimension<number>[];

  constructor(
    constraint: (props: IDispositionScenarioSessionFilterProps) => boolean,
    exclusionFilter: (
      props: IDispositionScenarioSessionFilterProps,
      options: CustomDimension<number>[]
    ) => CustomDimension<number>[]
  ) {
    this.constraint = constraint;
    this.exclusionFilter = exclusionFilter;
  }
}

export interface IDispositionScenarioFilterProps {
  /** Lead result type options which should be statically excluded in the scenario. */
  staticExclusions: LeadResultType[];
  /** Lead result type options which should be statically included in the scenario. */
  staticInclusions: LeadResultType[];
  /** Configuration for options which should be excluded in the scenario when the specified constraint is met. */
  conditionalExclusions: ScenarioConditionalExclusion[];
  /** Configuration for options which should be included in the scenario when the specified constraint is met. */
  conditionalInclusions: ScenarioConditionalInclusion[];
  /** Configuration for options which should be excluded that match the filter predicate in the scenario when
   * the specified constraint is met.
   */
  filteredExclusions: ScenarioFilteredExclusion[];
  /** Configuration for options which should be excluded that match the filter predicate in the scenario when
   * the specified constraint is met.
   */
  filteredInclusions: ScenarioFilteredInclusion[];
}

const getTypeFromOptions = (type: LeadResultType, options: CustomDimension<number>[]): CustomDimension<number> => {
  const locatedOption = options.find(o => o.id === EnvironmentConfiguration.leadResultIds[type]);
  if (!locatedOption) throw `Failed to located the lead result type by the name '${type}'`;
  return locatedOption;
};

/** Get the distinct list of options from the given list. */
const getDistinctOptions = (options: CustomDimension<number>[]): CustomDimension<number>[] => {
  const ids: Record<string, true> = {};
  const distinctOptions: CustomDimension<number>[] = [];
  options.forEach(o => {
    if (!ids[o.id]) {
      ids[o.id] = true;
      distinctOptions.push(o);
    }
  });
  return distinctOptions;
};

/** A configuration which filters the available lead result types selectable by the user for a given disposition scenario. */
class DispositionScenarioFilter {
  private staticExclusions: LeadResultType[];
  private staticInclusions: LeadResultType[];
  private conditionalExclusions: ScenarioConditionalExclusion[];
  private conditionalInclusions: ScenarioConditionalInclusion[];
  private filteredExclusions: ScenarioFilteredExclusion[];
  private filteredInclusions: ScenarioFilteredInclusion[];

  constructor(props: Partial<IDispositionScenarioFilterProps>) {
    this.staticExclusions = props.staticExclusions ?? [];
    this.staticInclusions = props.staticInclusions ?? [];
    this.conditionalExclusions = props.conditionalExclusions ?? [];
    this.conditionalInclusions = props.conditionalInclusions ?? [];
    this.filteredExclusions = props.filteredExclusions ?? [];
    this.filteredInclusions = props.filteredInclusions ?? [];
  }

  /** Aggregates the allowed lead result options from the current contextual properties and configured condition constraints.
   * @remarks The order of operations for the aggregation is:
   * 1. If there are configured inclusions, build the initial list of options from them.
   * 2. If no specified inclusion configurations, use the full list of options as the list of inclusions.
   * 3. If there are configured exclusions, remove those that satisfy constraints from the list of inclusions determined from step 1 & 2.
   * @param contextProps The contextual properties for the current session which may influence condition constraints.
   * @param leadResultTypeOptions The full list of options available during disposition.
   */
  getAllowedLeadResultTypes = (
    contextProps: IDispositionScenarioSessionFilterProps,
    options: CustomDimension<number>[]
  ): CustomDimension<number>[] => {
    let inclusions = this.staticInclusions.map(i => getTypeFromOptions(i, options));
    let exclusions = this.staticExclusions.map(i => getTypeFromOptions(i, options));

    this.conditionalInclusions.forEach(condition => {
      if (condition.constraint(contextProps))
        inclusions = inclusions.concat(condition.includedOptions.map(i => getTypeFromOptions(i, options)));
    });

    this.conditionalExclusions.forEach(condition => {
      if (condition.constraint(contextProps))
        exclusions = exclusions.concat(condition.excludedOptions.map(i => getTypeFromOptions(i, options)));
    });

    this.filteredInclusions.forEach(condition => {
      if (condition.constraint(contextProps))
        inclusions = inclusions.concat(condition.inclusionFilter(contextProps, options));
    });

    this.filteredExclusions.forEach(condition => {
      if (condition.constraint(contextProps))
        exclusions = exclusions.concat(condition.exclusionFilter(contextProps, options));
    });

    inclusions = getDistinctOptions(inclusions);
    exclusions = getDistinctOptions(exclusions);

    let finalAvailableSet =
      this.conditionalInclusions.length || this.staticInclusions.length || this.filteredInclusions.length
        ? inclusions
        : [...options];
    if (exclusions.length) finalAvailableSet = finalAvailableSet.filter(i => !exclusions.some(e => e.id === i.id));

    if (!finalAvailableSet.length)
      throw "A disposition scenario cannot result in 0 selectable lead result type options.";

    return finalAvailableSet;
  };
}

export default DispositionScenarioFilter;
